import Sentry from "@similarweb/sentry-logger";

import type { ContactWidgetData, WidgetConfig } from "app/pages/sales-intelligence/modules/ai-agent/types/superAgent";
import type { RevealedContactDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ContactsResponseDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";

import { findValidJsonBlock } from "./replaceWidgetJsonBlocks";

export type ContentSegment = { type: "markdown"; content: string } | { type: "widget"; config: WidgetConfig | null };

const OPENING_FENCE = /```json\r?\n/g;
const CLOSING_FENCE_LENGTH = 3;
const NOT_FOUND = -1;

const parseWidgetConfig = (jsonContent: string): WidgetConfig | null => {
    try {
        const parsed = JSON.parse(jsonContent.trim()) as Record<string, unknown> & {
            type?: string;
            data?: unknown;
            revealedContacts?: Record<string, RevealedContactDto>;
            contactsSearchData?: ContactsResponseDto;
        };
        if (parsed.type && parsed.data !== undefined && parsed.data !== null) {
            if (parsed.type === "widget_contacts") {
                const config: Extract<WidgetConfig, { type: "widget_contacts" }> = {
                    type: "widget_contacts",
                    data: parsed.data as ContactWidgetData,
                };
                if (parsed.revealedContacts) {
                    config.revealedContacts = parsed.revealedContacts;
                }
                if (parsed.contactsSearchData) {
                    config.contactsSearchData = parsed.contactsSearchData;
                }
                return config;
            }
            if (parsed.type === "widget_outreach_email" && parsed.data && typeof parsed.data === "object") {
                return { type: "widget_outreach_email", data: parsed.data } as WidgetConfig;
            }
            return { type: parsed.type, data: parsed.data } as WidgetConfig;
        }
    } catch (error) {
        Sentry.errorTracker({
            message: "Failed to parse widget config",
            e: error,
            level: "warning",
        });
    }
    return null;
};

export function parseContentWithWidgets(content: string): ContentSegment[] {
    if (!content.trim()) {
        return [{ type: "markdown", content }];
    }

    OPENING_FENCE.lastIndex = 0;
    const segments: ContentSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = OPENING_FENCE.exec(content)) !== null) {
        const jsonStart = match.index + match[0].length;
        const extracted = findValidJsonBlock(content, jsonStart, { allowTrailing: true });

        if (!extracted) {
            const fallbackFence = content.indexOf("```", jsonStart);
            const rawJson =
                fallbackFence !== NOT_FOUND ? content.slice(jsonStart, fallbackFence) : content.slice(jsonStart);

            const markdownContent = content.slice(lastIndex, match.index);
            if (markdownContent.trim()) {
                segments.push({ type: "markdown", content: markdownContent });
            }
            segments.push({ type: "widget", config: parseWidgetConfig(rawJson) });
            lastIndex = fallbackFence !== NOT_FOUND ? fallbackFence + CLOSING_FENCE_LENGTH : content.length;
            OPENING_FENCE.lastIndex = lastIndex;
            continue;
        }

        const markdownContent = content.slice(lastIndex, match.index);
        if (markdownContent.trim()) {
            segments.push({ type: "markdown", content: markdownContent });
        }

        const config = parseWidgetConfig(extracted.jsonContent);

        segments.push({ type: "widget", config });
        lastIndex = extracted.blockEnd;
        OPENING_FENCE.lastIndex = lastIndex;
    }

    const remaining = content.slice(lastIndex);
    if (remaining.trim()) {
        segments.push({ type: "markdown", content: remaining });
    }

    return segments.length > 0 ? segments : [{ type: "markdown", content }];
}
